// Read-only profile card driven by /api/v1/user?email=.
// Editing firstname/lastname requires a backend PATCH endpoint that
// is not yet available — see docs/backend-requirements/preferences-api.md.
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { UserRound } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Skeleton } from "@/components/ui/skeleton";

export function ProfileSection() {
  const { email, displayName, firstName, lastName, roles, isLoading } =
    useCurrentUser();

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader>
        <GlassCardTitle className="flex items-center gap-2">
          <UserRound className="h-5 w-5" />
          Profile
        </GlassCardTitle>
        <GlassCardDescription>
          Your account information from the directory.
        </GlassCardDescription>
      </GlassCardHeader>
      <GlassCardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage
              src={`https://api.dicebear.com/7.x/lorelei/svg?seed=${email || "default"}`}
              alt="User avatar"
            />
            <AvatarFallback>
              <UserRound className="h-6 w-6" />
            </AvatarFallback>
          </Avatar>
          <div className="space-y-1">
            {isLoading ? (
              <Skeleton className="h-5 w-40" />
            ) : (
              <p className="text-base font-semibold">
                {displayName ?? "Account"}
              </p>
            )}
            <p className="text-sm text-muted-foreground">{email ?? "—"}</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {roles.length > 0
                ? roles.map((r) => (
                    <Badge key={r} variant="secondary" className="text-[11px]">
                      {r}
                    </Badge>
                  ))
                : !isLoading && (
                    <span className="text-xs text-muted-foreground">
                      No roles assigned
                    </span>
                  )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="profile-firstname">First name</Label>
            <Input
              id="profile-firstname"
              value={firstName ?? ""}
              readOnly
              placeholder={isLoading ? "Loading…" : "—"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="profile-lastname">Last name</Label>
            <Input
              id="profile-lastname"
              value={lastName ?? ""}
              readOnly
              placeholder={isLoading ? "Loading…" : "—"}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Editing your name requires a backend update endpoint that isn't
          available yet. Contact your administrator to change these values.
        </p>
      </GlassCardContent>
    </GlassCard>
  );
}
